import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Percent } from "lucide-react";

import { api } from "../lib/api";
import { gbp, num } from "../lib/format";

/* LT-20 follow-up — what wastage adds to the materials cost, per category.
 * Estimate maths (qty × per-unit net / gross × rate), never the rounded
 * order quantities, so the gross column reconciles to the estimate's
 * materials total. Either `insights` is handed in (Order list tab, follows
 * its filters) or the panel fetches the whole-tender figures itself. */
const readOpen = (key) => { try { return localStorage.getItem(key) === "1"; } catch { return false; } };

function InsightsTable({ ins }) {
  const t = ins.total;
  return (
    <table className="w-full mt-3" data-testid="wastage-insights-table">
      <thead>
        <tr className="bg-surface-2">
          <th className="col-head text-left px-3 py-1.5">Category</th>
          <th className="col-head text-right px-3 py-1.5" style={{ width: 110 }}>Net £</th>
          <th className="col-head text-right px-3 py-1.5" style={{ width: 120 }}>Wastage £</th>
          <th className="col-head text-right px-3 py-1.5" style={{ width: 110 }}>Gross £</th>
          <th className="col-head text-right px-3 py-1.5" style={{ width: 90 }}>Wastage %</th>
          <th className="col-head text-right px-3 py-1.5" style={{ width: 90 }}>Share</th>
        </tr>
      </thead>
      <tbody>
        {ins.categories.map((c) => (
          <tr key={c.category_key} className="border-t border-line-soft" data-testid={`wastage-row-${c.category_key}`}>
            <td className="px-3 py-1.5 text-ink" style={{ fontSize: 12 }}>
              {c.category}
              <span className="text-ink-4" style={{ fontSize: 10.5 }}> · {c.material_count} material{c.material_count === 1 ? "" : "s"}, {c.line_count} line{c.line_count === 1 ? "" : "s"}</span>
            </td>
            <td className="px-3 py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{gbp(c.net_cost)}</td>
            <td className="px-3 py-1.5 text-right font-mono tnum text-ink" style={{ fontSize: 12, fontWeight: 600 }} data-testid={`wastage-cost-${c.category_key}`}>{gbp(c.wastage_cost)}</td>
            <td className="px-3 py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{gbp(c.gross_cost)}</td>
            <td className="px-3 py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{num(c.wastage_pct, 1)}%</td>
            <td className="px-3 py-1.5 text-right font-mono tnum text-ink-3" style={{ fontSize: 12 }}>{num(c.share_pct, 0)}%</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t-2 border-ink" data-testid="wastage-total-row">
          <td className="px-3 py-2 text-ink" style={{ fontSize: 12.5, fontWeight: 600 }}>All materials</td>
          <td className="px-3 py-2 text-right font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 600 }}>{gbp(t.net_cost)}</td>
          <td className="px-3 py-2 text-right font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 700 }} data-testid="wastage-total-cost">{gbp(t.wastage_cost)}</td>
          <td className="px-3 py-2 text-right font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 600 }} data-testid="wastage-total-gross">{gbp(t.gross_cost)}</td>
          <td className="px-3 py-2 text-right font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 600 }}>{num(t.wastage_pct, 1)}%</td>
          <td />
        </tr>
      </tfoot>
    </table>
  );
}

export function WastageInsightsPanel({ tenderId, insights, estimateMaterialsTotal, refreshKey, storageKey = "lt20_wastage_open", filtered = false }) {
  const [open, setOpen] = useState(() => readOpen(storageKey));
  const [fetched, setFetched] = useState(null);
  const ins = insights || fetched;

  useEffect(() => {
    if (insights || !tenderId || refreshKey === null) return undefined;
    let alive = true;
    api.get(`/tenders/${tenderId}/wastage-insights`)
      .then((r) => { if (alive) setFetched(r.data); })
      .catch(() => { if (alive) setFetched(null); });
    return () => { alive = false; };
  }, [tenderId, insights, refreshKey]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
  };

  if (!ins || !ins.categories.length) return null;
  const t = ins.total;
  const estTotal = estimateMaterialsTotal ?? ins.estimate_materials_total;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="rounded-lg border border-line bg-surface" data-testid="wastage-insights-panel">
      <button type="button" onClick={toggle} className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-surface-2/60 transition-colors" data-testid="wastage-insights-toggle" aria-expanded={open}>
        <span className="inline-flex items-center gap-2 text-ink" style={{ fontSize: 12.5 }}>
          <Percent size={13} className="text-ink-3" />
          <span>
            Wastage adds <span className="font-mono tnum" style={{ fontWeight: 700 }} data-testid="wastage-summary-cost">{gbp(t.wastage_cost)}</span>
            {" "}(<span className="font-mono tnum" data-testid="wastage-summary-pct">{num(t.wastage_pct, 1)}%</span>) to materials
            {filtered ? " in this selection" : ""}
          </span>
        </span>
        <span className="inline-flex items-center gap-1 text-ink-3" style={{ fontSize: 11.5 }}>
          {open ? "Hide breakdown" : "Show breakdown"} <Chevron size={13} />
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3 border-t border-line-soft">
          <InsightsTable ins={ins} />
          <p className="text-ink-4 mt-2" style={{ fontSize: 10.5 }} data-testid="wastage-insights-note">
            Estimate maths — qty × per-unit net and gross × rate; order-list rounding is not included.
            {estTotal != null && !filtered ? ` Estimate materials total ${gbp(estTotal)}; pence differences are the estimate's per-unit rounding.` : ""}
            {ins.rate_override_line_count > 0 ? ` ${ins.rate_override_line_count} line(s) with a manual material-rate override are shown at their recipe cost.` : ""}
          </p>
        </div>
      )}
    </div>
  );
}
